/*
 * Cart-wide sample quantity limit.
 * A sample is any cart line whose SKU contains "sample" (case-insensitive).
 * The limit applies to the combined quantity of all sample lines, not to each product.
 * Settings and strings come from window.sampleLimit (layout/theme.liquid).
 */
(function () {
  if (window.SampleLimit) return;

  const config = window.sampleLimit || {};
  const strings = config.strings || {};
  const LIMIT = parseInt(config.max, 10) || 10;
  const BLOCK_ONLY_WHEN_EXCEEDED = config.checkoutRule === 'exceeded';

  const CHECKOUT_SELECTOR = '[data-sample-checkout]';
  const BLOCKED_CHECKOUT_SELECTOR = '[data-sample-checkout][aria-disabled="true"]';
  const WARNING_SELECTOR = '[data-sample-limit-warning]';
  const DYNAMIC_CHECKOUT_SELECTOR = '.cart__dynamic-checkout-buttons';

  function isSampleItem(item) {
    return String((item && item.sku) || '').toLowerCase().includes('sample');
  }

  function countSamples(items) {
    return (items || []).reduce((total, item) => (isSampleItem(item) ? total + item.quantity : total), 0);
  }

  function isCheckoutBlocked(total) {
    return BLOCK_ONLY_WHEN_EXCEEDED ? total > LIMIT : total >= LIMIT;
  }

  function fetchCart() {
    const cartUrl = (window.routes && window.routes.cart_url) || '/cart';
    return fetch(`${cartUrl}.js`, { headers: { Accept: 'application/json' } }).then((response) => {
      if (!response.ok) throw new Error('Unable to load cart');
      return response.json();
    });
  }

  // Resolves to true when `quantity` more sample items fit within the cart-wide limit.
  // If the cart cannot be read, the add is allowed so the existing add-to-cart flow is never blocked by a network error.
  function canAdd(quantity) {
    const requested = parseInt(quantity, 10) || 1;
    return fetchCart()
      .then((cart) => countSamples(cart.items) + requested <= LIMIT)
      .catch((error) => {
        console.warn('Sample limit check skipped:', error);
        return true;
      });
  }

  function isSampleVariant(productForm, variantId) {
    const ids = (productForm && productForm.dataset.sampleVariantIds) || '';
    return Boolean(variantId) && ids.split(',').includes(String(variantId));
  }

  // Shows the "limit reached" message right after the element the customer interacted with.
  function showAddMessage(anchorElement) {
    if (!anchorElement) return;
    const anchor = anchorElement.closest('.sample-button') || anchorElement;
    let message = anchor.nextElementSibling;

    if (!message || !message.hasAttribute('data-sample-limit-add')) {
      message = document.createElement('p');
      message.className = 'sample-limit-message';
      message.setAttribute('role', 'alert');
      message.setAttribute('data-sample-limit-add', '');
      anchor.insertAdjacentElement('afterend', message);
    }

    message.textContent = strings.addLimit || `You can add up to ${LIMIT} sample items in total.`;
    message.hidden = false;
  }

  function showCheckoutWarning(button) {
    const container = button.closest('.cart__ctas') || button.parentElement;
    const warning = container && container.querySelector(WARNING_SELECTOR);
    if (!warning) return;
    warning.hidden = false;
  }

  function syncDynamicCheckoutButtons(blocked) {
    document.querySelectorAll(DYNAMIC_CHECKOUT_SELECTOR).forEach((element) => {
      element.classList.toggle('sample-limit-hidden', blocked);
    });
  }

  // Updates every checkout button from the latest cart data. Needed on the cart page,
  // where the checkout button is not part of the HTML re-rendered after a quantity change.
  function refresh(cart) {
    const blocked = isCheckoutBlocked(countSamples(cart.items));

    document.querySelectorAll(CHECKOUT_SELECTOR).forEach((button) => {
      if (blocked) {
        button.setAttribute('aria-disabled', 'true');
      } else {
        button.removeAttribute('aria-disabled');
        const container = button.closest('.cart__ctas') || button.parentElement;
        const warning = container && container.querySelector(WARNING_SELECTOR);
        if (warning) warning.hidden = true;
      }
    });

    syncDynamicCheckoutButtons(blocked);
  }

  function preventBlockedCheckout(event, button) {
    event.preventDefault();
    event.stopImmediatePropagation();
    showCheckoutWarning(button);
  }

  // Capture phase so the blocked checkout never reaches the form submit or other click handlers.
  document.addEventListener(
    'click',
    (event) => {
      const button = event.target.closest && event.target.closest(BLOCKED_CHECKOUT_SELECTOR);
      if (button) preventBlockedCheckout(event, button);
    },
    true
  );

  // Covers implicit submission, such as pressing Enter inside the cart form.
  document.addEventListener(
    'submit',
    (event) => {
      const submitter = event.submitter;
      if (submitter && submitter.matches(BLOCKED_CHECKOUT_SELECTOR)) preventBlockedCheckout(event, submitter);
    },
    true
  );

  document.addEventListener('DOMContentLoaded', () => {
    if (document.querySelector(CHECKOUT_SELECTOR)) {
      syncDynamicCheckoutButtons(Boolean(document.querySelector(BLOCKED_CHECKOUT_SELECTOR)));
    }
  });

  if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
    subscribe(PUB_SUB_EVENTS.cartUpdate, () => {
      if (!document.querySelector(CHECKOUT_SELECTOR)) return;
      fetchCart()
        .then(refresh)
        .catch((error) => console.warn('Sample limit refresh skipped:', error));
    });
  }

  window.SampleLimit = {
    limit: LIMIT,
    isSampleItem,
    countSamples,
    canAdd,
    isSampleVariant,
    showAddMessage,
    refresh,
  };
})();
